// src/components/EditMemberPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { auth, db } from '../firebase'; // Assuming firebase config is exported from here
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { Form, Button, Container, Row, Col, Card, Spinner, Alert, Breadcrumb, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faPhotoFilm } from '@fortawesome/free-solid-svg-icons';
import { isValid, parse } from 'date-fns';
import MemberPhotoAvatar from './MemberPhotoAvatar'; // Import the avatar component

// --- Helper: Fetch orgId once ---
async function getOrgId() {
    if (!auth.currentUser) { return null; }
    try {
        const idTokenResult = await auth.currentUser.getIdTokenResult(false);
        return idTokenResult.claims.orgId || null;
    } catch (error) {
        console.error("getOrgId (EditMember): Error getting custom claims token:", error);
        return null;
    }
}

// --- Helper: Fetch Family Name ---
async function fetchFamilyName(familyId) {
    if (!familyId) return 'Unknown Family';
    try {
        const familyRef = doc(db, 'family', familyId);
        const familySnap = await getDoc(familyRef);
        return familySnap.exists() ? (familySnap.data().surname || 'Unnamed Family') : 'Unknown Family (Not Found)';
    } catch (error) {
        console.error(`WorkspaceFamilyName: Error fetching family name for ID ${familyId}:`, error);
        return 'Unknown Family (Error)';
    }
}

// --- Helper functions for birthday dropdowns ---
const getMonths = () => Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: new Date(0, i).toLocaleString('default', { month: 'long' }) }));
const getDaysInMonth = (month, year) => {
    if (!month) return Array.from({ length: 31 }, (_, i) => ({ value: i + 1, label: (i + 1).toString() }));
    const yearToUse = (year && parseInt(year, 10)) ? parseInt(year, 10) : 2000;
    const days = new Date(yearToUse, parseInt(month, 10), 0).getDate();
    return Array.from({ length: days }, (_, i) => ({ value: i + 1, label: (i + 1).toString() }));
};
const getYears = () => {
    const years = [{ value: '', label: 'Year' }];
    const currentYear = new Date().getFullYear();
    for (let i = currentYear; i >= currentYear - 120; i--) {
        years.push({ value: i, label: i.toString() });
    }
    return years;
};


// --- Main Component: EditMemberPage ---
function EditMemberPage() {
    const { memberId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    // State for form fields
    const [memberName, setMemberName] = useState('');
    const [selectedBirthMonth, setSelectedBirthMonth] = useState('');
    const [selectedBirthDay, setSelectedBirthDay] = useState('');
    const [selectedBirthYear, setSelectedBirthYear] = useState('');
    const [cellphone, setCellphone] = useState('');
    const [email, setEmail] = useState('');
    const [familyId, setFamilyId] = useState('');
    const [familyName, setFamilyName] = useState('');

    // State for photo handling
    const [initialPhotoPath, setInitialPhotoPath] = useState(null);
    const [currentPhotoPath, setCurrentPhotoPath] = useState(null);

    // State for component status
    const [loadingMember, setLoadingMember] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [userOrgId, setUserOrgId] = useState(null);

    const firebaseFunctions = getFunctions();
    const updateMemberV2Func = httpsCallable(firebaseFunctions, 'updateMemberV2');

    // --- Effect 1: Fetch orgId ---
    useEffect(() => {
        let isMounted = true;
        getOrgId().then(orgId => {
            if (isMounted) {
                if (orgId) setUserOrgId(orgId);
                else {
                    setError("Could not determine organization ID. Please ensure you are logged in.");
                    setLoadingMember(false);
                }
            }
        });
        return () => { isMounted = false; };
    }, []);

    // --- Effect 2: Check for returned photo path from navigation state ---
    useEffect(() => {
        if (location.state?.newPhotoPath) {
            setCurrentPhotoPath(location.state.newPhotoPath === 'REMOVED' ? null : location.state.newPhotoPath);
            navigate(location.pathname, { replace: true, state: {} }); // Clear state
        }
    }, [location.state, navigate, location.pathname]);


    // --- Effect 3: Fetch Member Data ---
    const fetchMemberData = useCallback(async () => {
        if (!userOrgId || !memberId) return;

        setLoadingMember(true); setError(null);
        let isMounted = true;

        try {
            const memberRef = doc(db, 'member', memberId);
            const memberSnap = await getDoc(memberRef);

            if (!memberSnap.exists()) {
                 if (isMounted) { setError("Member not found."); setLoadingMember(false); }
                 return;
            }
            const data = memberSnap.data();
            if (data.orgId !== userOrgId) {
                 if (isMounted) { setError("You do not have permission to edit this member."); setLoadingMember(false); }
                 return;
            }

            if (isMounted) {
                setMemberName(data.name || '');
                setCellphone(data.cellphone || '');
                setEmail(data.email || '');
                setFamilyId(data.familyId || '');

                setInitialPhotoPath(data.photo || null);
                // Only set current path if it hasn't been updated by navigation state already
                if (location.state?.newPhotoPath === undefined) {
                    setCurrentPhotoPath(data.photo || null);
                }

                if (data.familyId) {
                    fetchFamilyName(data.familyId).then(fName => {
                        if (isMounted) setFamilyName(fName);
                    });
                } else {
                    setFamilyName('No Associated Family');
                }

                // Populate Birthday Dropdowns
                const bMonth = data.birth_month ? String(data.birth_month).trim() : '';
                const bDay = data.birth_day ? String(data.birth_day).trim() : '';
                const bYear = data.birth_year ? String(data.birth_year).trim() : '';
                setSelectedBirthMonth(bMonth);
                setSelectedBirthDay(bDay);
                setSelectedBirthYear((bMonth && bDay) ? bYear : ''); // Year only valid if month/day exist

                setLoadingMember(false);
            }
        } catch (err) {
            console.error(`EditMemberPage: Error fetching member data for ${memberId}:`, err);
            if (isMounted) { setError("Failed to load member details."); setLoadingMember(false); }
        }
    }, [memberId, userOrgId, location.state]);

    useEffect(() => {
        fetchMemberData();
    }, [fetchMemberData]);

    // --- Validation and Submission ---
    const validateBirthday = () => {
        // ... (validation logic remains the same)
        return true;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError(null); setSuccessMessage(null);

        if (!memberName.trim()) { setError("Member name is required."); return; }
        if (!validateBirthday()) return;
        setSubmitting(true);

        try {
            const payload = {
                memberId: memberId,
                name: memberName.trim(),
                birth_month: selectedBirthMonth || null,
                birth_day: selectedBirthDay || null,
                birth_year: selectedBirthYear || null,
                cellphone: cellphone.trim() || null,
                email: email.trim() || null,
            };

            // Only include photo if it has changed
            if (currentPhotoPath !== initialPhotoPath) {
                payload.photo = currentPhotoPath;
            }

            const result = await updateMemberV2Func(payload);

            if (result.data?.success) {
                setSuccessMessage(result.data.message || "Member updated successfully!");
                setTimeout(() => navigate(familyId ? `/families/${familyId}/member/${memberId}` : '/families'), 2000);
            } else {
                throw new Error(result.data?.message || "Failed to update member.");
            }
        } catch (err) {
            console.error("EditMemberPage: Error calling 'updateMemberV2' function:", err);
            setError(err.message || "An unexpected error occurred.");
        } finally {
            setSubmitting(false);
        }
    };

    const availableMonths = useMemo(() => getMonths(), []);
    const availableDays = useMemo(() => getDaysInMonth(selectedBirthMonth, selectedBirthYear), [selectedBirthMonth, selectedBirthYear]);
    const availableYears = useMemo(() => getYears(), []);


    // --- Render Logic ---
    return (
        <Container fluid className="mt-3 mb-5">
             <Row className="justify-content-center">
                <Col md={8} lg={6}>
                     <Breadcrumb>
                        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/dashboard" }}>Dashboard</Breadcrumb.Item>
                        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/families" }}>Families</Breadcrumb.Item>
                         {familyId && familyName && !loadingMember && (
                             <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/families/${familyId}` }}>{familyName} Family</Breadcrumb.Item>
                         )}
                         <Breadcrumb.Item active>Edit: {memberName || '...'}</Breadcrumb.Item>
                     </Breadcrumb>

                    <Card>
                         <Card.Header as="h3" className="text-center">Edit Member</Card.Header>
                        <Card.Body>
                            {error && <Alert variant="danger">{error}</Alert>}
                            {successMessage && <Alert variant="success">{successMessage}</Alert>}

                            {loadingMember ? (
                                <div className="text-center p-4"><Spinner animation="border" /><p className="mt-2">Loading member data...</p></div>
                            ) : (
                                <Form onSubmit={handleSubmit}>
                                    {/* --- Photo Section --- */}
                                    <div className="text-center mb-3">
                                        <MemberPhotoAvatar src={currentPhotoPath} alt={memberName} size={120} />
                                    </div>
                                    <div className="d-grid mb-4">
                                        <Button
                                            as={Link}
                                            to={`/families/${familyId}/assign-photos`}
                                            state={{ forMemberId: memberId, forMemberName: memberName, currentPhotoPath: currentPhotoPath, returnPath: location.pathname }}
                                            variant="outline-primary"
                                            disabled={submitting || !familyId}
                                        >
                                            <FontAwesomeIcon icon={faPhotoFilm} className="me-2" />
                                            Change Member Photo
                                        </Button>
                                    </div>

                                    {/* --- Form Fields (Reordered) --- */}
                                    <Form.Group className="mb-3">
                                        <Form.Label>Family</Form.Label>
                                        <Form.Control plaintext readOnly value={familyName || 'Loading...'} />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                                        <Form.Control type="text" value={memberName} onChange={(e) => setMemberName(e.target.value)} required disabled={submitting} />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Email Address</Form.Label>
                                        <Form.Control type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={submitting} />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Cellphone</Form.Label>
                                        <Form.Control type="tel" value={cellphone} onChange={(e) => setCellphone(e.target.value)} disabled={submitting} />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>Birthday</Form.Label>
                                        <Row>
                                            <Col sm={5}>
                                                <Form.Select value={selectedBirthMonth} onChange={(e) => setSelectedBirthMonth(e.target.value)} disabled={submitting} aria-label="Birth Month">
                                                    <option value="">Month</option>
                                                    {availableMonths.map(m => <option key={`m-${m.value}`} value={m.value}>{m.label}</option>)}
                                                </Form.Select>
                                            </Col>
                                            <Col sm={3}>
                                                <Form.Select value={selectedBirthDay} onChange={(e) => setSelectedBirthDay(e.target.value)} disabled={submitting || !selectedBirthMonth} aria-label="Birth Day">
                                                    <option value="">Day</option>
                                                    {availableDays.map(d => <option key={`d-${d.value}`} value={d.value}>{d.label}</option>)}
                                                </Form.Select>
                                            </Col>
                                            <Col sm={4}>
                                                <Form.Select value={selectedBirthYear} onChange={(e) => setSelectedBirthYear(e.target.value)} disabled={submitting} aria-label="Birth Year">
                                                    {availableYears.map(y => <option key={`y-${y.value}`} value={y.value}>{y.label}</option>)}
                                                </Form.Select>
                                            </Col>
                                        </Row>
                                    </Form.Group>

                                    <div className="d-grid">
                                        <Button variant="primary" type="submit" disabled={submitting || loadingMember}>
                                            {submitting ? <><Spinner as="span" size="sm" /> Updating...</> : 'Update Member'}
                                        </Button>
                                    </div>
                                </Form>
                             )}
                        </Card.Body>
                    </Card>
                 </Col>
             </Row>
        </Container>
    );
}

export default EditMemberPage;
